/**
 * Clipboard Position Utilities
 * 
 * Functions for calculating positions and collecting nested sprint data.
 */

/**
 * Calculate relative positions from absolute positions
 * Returns positions normalized so the top-left node is at (0, 0)
 */
export function calculateRelativePositions(tasks, nodePositions) {
  const positions = {};
  tasks.forEach(task => {
    positions[task.id] = nodePositions[task.id] || { x: task.x || 0, y: task.y || 0 };
  });
  
  const posArray = Object.values(positions);
  if (posArray.length === 0) return {};
  
  const minX = Math.min(...posArray.map(p => p.x));
  const minY = Math.min(...posArray.map(p => p.y));
  
  const relativePositions = {};
  tasks.forEach(task => {
    const pos = positions[task.id];
    relativePositions[task.id] = {
      x: pos.x - minX,
      y: pos.y - minY,
    };
  });
  
  return relativePositions;
}

/**
 * Prefer the sprint object that has a loaded tasks array (parent nested rows are often shallow).
 * The pool may contain duplicates (metadata + fully-loaded) — always pick the deepest copy.
 */
function pickNestedSprintRecord(nestedSprintId, nestedSprints, allSprints) {
  const candidates = [
    ...(nestedSprints || []).filter(s => s?.id === nestedSprintId),
    ...(allSprints || []).filter(s => s?.id === nestedSprintId),
  ];
  if (candidates.length === 0) return null;
  return candidates.find(c => Array.isArray(c.tasks)) ?? candidates[0];
}

/**
 * Attach last-chance probed sprint payloads to `out` by walking the task tree.
 * Uses the best available sprint record (probed > out > pool) so deep grandchildren
 * are reachable even when intermediate parents are still metadata-only in `out`.
 */
function mergeProbedSprintsIntoOut(rootTasks, out, probedBySprintId, sprintPool) {
  const resolveForTask = (task) => {
    if (task.nestedSprintId && probedBySprintId[task.nestedSprintId]) {
      return probedBySprintId[task.nestedSprintId];
    }
    const fromOut = out[task.id];
    if (fromOut && Array.isArray(fromOut.tasks)) return fromOut;
    if (task.nestedSprintId) {
      const fromPool = sprintPool.find(s => s.id === task.nestedSprintId);
      if (fromPool && Array.isArray(fromPool.tasks)) return fromPool;
    }
    return fromOut;
  };

  const walk = (taskList) => {
    for (const task of taskList) {
      if (task.nestedSprintId && probedBySprintId[task.nestedSprintId]) {
        out[task.id] = probedBySprintId[task.nestedSprintId];
      }
    }
    for (const task of taskList) {
      const nested = resolveForTask(task);
      if (nested && Array.isArray(nested.tasks)) {
        walk(nested.tasks);
      }
    }
  };
  walk(rootTasks);
}

/** Force-link probed sprints onto every task that references them (any depth). */
function forceAttachProbedReferrers(rootTasks, out, probedBySprintId, pool) {
  const seenLists = new Set();
  const taskLists = [];

  const addList = (list) => {
    if (!Array.isArray(list) || seenLists.has(list)) return;
    seenLists.add(list);
    taskLists.push(list);
  };

  addList(rootTasks);
  for (const sprint of [...Object.values(out), ...Object.values(probedBySprintId), ...pool]) {
    if (sprint && Array.isArray(sprint.tasks)) addList(sprint.tasks);
  }

  for (const [nestedId, probe] of Object.entries(probedBySprintId)) {
    for (const taskList of taskLists) {
      for (const task of taskList) {
        if (task.nestedSprintId === nestedId) {
          out[task.id] = probe;
        }
      }
    }
  }
}

/**
 * Collect nested sprint data for tasks that have nested sprints (all depths).
 * @param {Array} allSprints - Optional; used to resolve full sprint rows when parent nested list is shallow
 */
export function collectNestedSprintData(tasks, nestedSprints, allSprints = []) {
  const { nestedSprintData } = collectNestedSprintDataWithStatus(tasks, nestedSprints, allSprints);
  return nestedSprintData;
}

/**
 * Like collectNestedSprintData, but also reports any nested campaigns that are
 * referenced but could not be resolved to a record with a loaded `tasks` array.
 *
 * @returns {{ nestedSprintData: Object, missingNestedSprintIds: string[] }}
 *   missingNestedSprintIds = referenced nestedSprintIds whose record is absent
 *   OR present but without a loaded `tasks` array (so their descendants can't be
 *   traversed). An incomplete copy would silently drop these subtrees.
 */
export function collectNestedSprintDataWithStatus(tasks, nestedSprints, allSprints = []) {
  const nestedSprintData = {};
  const visitedSprintIds = new Set();
  const missingNestedSprintIds = new Set();

  const collectFromTaskList = (taskList) => {
    taskList.forEach(task => {
      if (!task.nestedSprintId) return;

      const nested = pickNestedSprintRecord(task.nestedSprintId, nestedSprints, allSprints);
      if (!nested) {
        missingNestedSprintIds.add(task.nestedSprintId);
        return;
      }

      // Already visited this nested id — upgrade if we now have a fully-loaded record.
      if (visitedSprintIds.has(task.nestedSprintId)) {
        if (Array.isArray(nested.tasks)) {
          const prevEntry = Object.entries(nestedSprintData).find(([, s]) => s.id === task.nestedSprintId);
          if (prevEntry && !Array.isArray(prevEntry[1].tasks)) {
            nestedSprintData[prevEntry[0]] = nested;
            missingNestedSprintIds.delete(task.nestedSprintId);
            collectFromTaskList(nested.tasks);
          }
        }
        return;
      }

      visitedSprintIds.add(task.nestedSprintId);
      nestedSprintData[task.id] = nested;
      if (Array.isArray(nested.tasks)) {
        collectFromTaskList(nested.tasks);
      } else {
        missingNestedSprintIds.add(task.nestedSprintId);
      }
    });
  };

  collectFromTaskList(tasks);
  return {
    nestedSprintData,
    missingNestedSprintIds: Array.from(missingNestedSprintIds),
  };
}

/**
 * Like collectNestedSprintData, but fetches full sprint payloads when tasks are not in memory yet.
 *
 * @returns {Promise<{ nestedSprintData: Object, missingNestedSprintIds: string[], danglingNestedSprintIds: string[] }>}
 *   missingNestedSprintIds = referenced nested campaigns that loadFullSprint
 *     CAN return with a tasks array, but still couldn't be collected - a genuine
 *     transient problem. The caller should abort and prompt a retry.
 *   danglingNestedSprintIds = referenced ids where loadFullSprint fails or
 *     returns no tasks (deleted campaign or stale metadata ghost). Dead links
 *     that are harmlessly dropped on paste - they DON'T block a copy.
 */
export async function collectNestedSprintDataWithLoad(tasks, nestedSprints, allSprints, loadFullSprint) {
  if (typeof loadFullSprint !== 'function') {
    const { nestedSprintData, missingNestedSprintIds } = collectNestedSprintDataWithStatus(tasks, nestedSprints, allSprints);
    return { nestedSprintData, missingNestedSprintIds, danglingNestedSprintIds: [] };
  }

  const sprintPool = [...(nestedSprints || []), ...(allSprints || [])];
  let out = collectNestedSprintData(tasks, sprintPool, allSprints);

  for (let pass = 0; pass < 12; pass++) {
    let changed = false;

    for (const [taskId, sprint] of Object.entries({ ...out })) {
      if (!Array.isArray(sprint.tasks)) {
        const loaded = await loadFullSprint(sprint.id);
        if (loaded && Array.isArray(loaded.tasks)) {
          out[taskId] = loaded;
          changed = true;
        }
      }
    }

    const expanded = collectNestedSprintData(tasks, [...sprintPool, ...Object.values(out)], allSprints);
    for (const [taskId, sprint] of Object.entries(expanded)) {
      if (!out[taskId] || (Array.isArray(sprint.tasks) && !Array.isArray(out[taskId]?.tasks))) {
        out[taskId] = sprint;
        changed = true;
      }
    }

    if (!changed) break;
  }

  // Last-chance: probe anything still unresolved. Metadata-only ghosts (deleted
  // campaigns still listed in the sidebar) must NOT block copy forever — only
  // ids that loadFullSprint actually returns with tasks are worth blocking on.
  let { missingNestedSprintIds: stillMissing } = collectNestedSprintDataWithStatus(
    tasks,
    [...sprintPool, ...Object.values(out)],
    allSprints,
  );

  const probedBySprintId = {};
  for (const id of stillMissing) {
    try {
      const probe = await loadFullSprint(id);
      if (probe && Array.isArray(probe.tasks)) {
        probedBySprintId[id] = probe;
      }
    } catch {
      // Dead link — load failed, will classify as dangling below.
    }
  }

  const fullPool = () => [...sprintPool, ...Object.values(out), ...Object.values(probedBySprintId)];

  if (Object.keys(probedBySprintId).length > 0) {
    mergeProbedSprintsIntoOut(tasks, out, probedBySprintId, fullPool());
    const expanded = collectNestedSprintData(tasks, fullPool(), allSprints);
    for (const [taskId, sprint] of Object.entries(expanded)) {
      if (!out[taskId] || (Array.isArray(sprint.tasks) && !Array.isArray(out[taskId]?.tasks))) {
        out[taskId] = sprint;
      }
    }
    // Extra passes after probe — large multi-nested trees often need one more expansion.
    for (let pass = 0; pass < 4; pass++) {
      let changed = false;
      for (const [taskId, sprint] of Object.entries({ ...out })) {
        if (!Array.isArray(sprint.tasks)) {
          const loaded = await loadFullSprint(sprint.id);
          if (loaded && Array.isArray(loaded.tasks)) {
            out[taskId] = loaded;
            changed = true;
          }
        }
      }
      const more = collectNestedSprintData(tasks, fullPool(), allSprints);
      for (const [taskId, sprint] of Object.entries(more)) {
        if (!out[taskId] || (Array.isArray(sprint.tasks) && !Array.isArray(out[taskId]?.tasks))) {
          out[taskId] = sprint;
          changed = true;
        }
      }
      if (!changed) break;
    }
    forceAttachProbedReferrers(tasks, out, probedBySprintId, fullPool());
  }

  stillMissing = collectNestedSprintDataWithStatus(
    tasks,
    fullPool(),
    allSprints,
  ).missingNestedSprintIds;

  const missingNestedSprintIds = [];
  const danglingNestedSprintIds = [];
  for (const id of stillMissing) {
    const attachedInOut = Object.values(out).some(
      s => s?.id === id && Array.isArray(s.tasks),
    );
    if (attachedInOut) {
      // Fully loaded and linked — status check was conservative, copy is safe.
      continue;
    }
    if (probedBySprintId[id]) {
      // API returned full data but we couldn't link it to the copy tree — block.
      missingNestedSprintIds.push(id);
    } else {
      // No loadable payload: deleted campaign or stale nestedSprintId on mission.
      danglingNestedSprintIds.push(id);
    }
  }

  return { nestedSprintData: out, missingNestedSprintIds, danglingNestedSprintIds };
}
